import {
  FETCH_USER,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILED,
  USER_SIGN_IN,
  USER_SIGN_IN_FAILED,
  USER_SIGN_OUT,
  CLEAR_LOGIN_ERROR,
  REQUEST_OTP,
  REQUEST_OTP_SUCCESS,
  REQUEST_OTP_FAILED,
  UPDATE_USER_WALLET_HISTORY,
  SEND_RESET_EMAIL,
} from "../store/types";

import store from "../store/store";
import { firebase } from "../config/configureFirebase";
import { onValue, update, set, off } from "firebase/database";
import { onAuthStateChanged, signInWithCredential, signInWithPopup, signOut, signInWithEmailAndPassword, signInWithCustomToken, sendPasswordResetEmail } from "firebase/auth";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import base64 from "react-native-base64";
import getCurrentUid, { getNativeAuth, getNativeIdToken } from '../other/getCurrentUid';
import { safeUpdate, safeSet } from '../other/firebaseDbHelper';

// Optional AsyncStorage for profile caching (only on React Native)
let AsyncStorage = null;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (e) {}
const CACHED_PROFILE_KEY = 'cached_user_profile';
const SKIP_CACHED_PROFILE_ONCE_KEY = 'skip_cached_profile_once';

const OTP_REQUEST_TIMEOUT_MS = 20000;
const OTP_VERIFY_TIMEOUT_MS = 15000;
const CUSTOM_TOKEN_SIGN_IN_TIMEOUT_MS = 15000;
const AUTH_BOOTSTRAP_GRACE_MS = 2500;
const AUTH_BOOTSTRAP_POLL_MS = 150;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getBootstrapAuthStrings = () => {
  const defaultLanguage = store.getState().languagedata.defaultLanguage || {};
  return {
    authError: defaultLanguage.auth_error || "Authentication error",
    notLoggedIn: defaultLanguage.not_logged_in || "Not logged in",
  };
};

const waitForNativeAuthUser = async (timeoutMs = AUTH_BOOTSTRAP_GRACE_MS) => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() <= deadline) {
    const nativeUser = getNativeAuth()?.currentUser;
    if (nativeUser) {
      return nativeUser;
    }
    await delay(AUTH_BOOTSTRAP_POLL_MS);
  }

  return null;
};

const dispatchNotLoggedInIfStillSignedOut = (dispatch, auth) => {
  if (auth?.currentUser || getNativeAuth()?.currentUser) {
    return;
  }

  const bootstrapStrings = getBootstrapAuthStrings();
  dispatch({
    type: FETCH_USER_FAILED,
    payload: { code: bootstrapStrings.authError, message: bootstrapStrings.notLoggedIn },
  });
};

const getFirebaseAuthPersistenceKeys = (config, appName = '[DEFAULT]') => {
  if (!config?.apiKey) {
    return [];
  }

  return [
    `firebase:authUser:${config.apiKey}:${appName}`,
    `firebase:authEvent:${config.apiKey}:${appName}`,
    `firebase:redirectUser:${config.apiKey}:${appName}`,
    `firebase:persistence:${config.apiKey}:${appName}`,
    `firebase:pendingRedirect:${config.apiKey}:${appName}`,
  ];
};

const normalizeActionError = (error, fallbackMessage) => {
  if (typeof error === "string") {
    return { message: error };
  }

  if (error && typeof error === "object") {
    return {
      message: error.message || error.error || fallbackMessage,
      code: error.code || null,
      status: error.status || null,
      details: error.details || null,
    };
  }

  return { message: fallbackMessage };
};

const maskMobileForLog = (value) => {
  if (!value) return null;
  const str = String(value);
  return `${'*'.repeat(Math.max(0, str.length - 4))}${str.slice(-4)}`;
};

const fetchJsonWithTimeout = async (url, options, timeoutMs, fallbackMessage) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    const rawText = await response.text();
    let result = {};

    if (rawText) {
      try {
        result = JSON.parse(rawText);
      } catch (error) {
        result = { error: rawText };
      }
    }

    if (!response.ok) {
      throw {
        message: result.error || fallbackMessage,
        details: result.details || null,
        status: response.status,
      };
    }

    return result;
  } catch (error) {
    if (error?.name === "AbortError") {
      throw {
        message: fallbackMessage,
        code: "REQUEST_TIMEOUT",
      };
    }

    throw normalizeActionError(error, fallbackMessage);
  } finally {
    clearTimeout(timeoutId);
  }
};

const getNativeStorage = () => {
  try {
    return require('@react-native-firebase/storage').default;
  } catch (error) {
    return null;
  }
};

const uriToUploadBlob = (uri) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => resolve(xhr.response);
    xhr.onerror = () => reject(new Error("URI_TO_BLOB_FAILED"));
    xhr.responseType = "blob";
    xhr.open("GET", uri, true);
    xhr.send(null);
  });

const uploadProfileDocument = async (webRef, nativePath, fileData, defaultContentType) => {
  const nativeStorage = getNativeStorage();
  const contentType = fileData?.mimeType || defaultContentType;

  if (nativeStorage && fileData?.uri) {
    const nativeRef = nativeStorage().ref(nativePath);
    await nativeRef.putFile(fileData.uri, {
      contentType,
    });
    return await nativeRef.getDownloadURL();
  }

  if (fileData?.uri) {
    const uploadBlob = await uriToUploadBlob(fileData.uri);
    await uploadBytesResumable(webRef, uploadBlob, { contentType });
    uploadBlob?.close?.();
  } else {
    await uploadBytesResumable(webRef, fileData, contentType ? { contentType } : undefined);
    fileData?.close?.();
  }

  return await getDownloadURL(webRef);
};

const buildProfileSeedFromUser = (user) => {
  let data = {
    uid: user.uid,
    mobile: "",
    email: "",
    firstName: "",
    lastName: "",
    verifyId: "",
  };

  if (user.providerData.length == 0 && user.email) {
    data.email = user.email;
  }
  if (user.providerData.length > 0 && user.phoneNumber) {
    data.mobile = user.phoneNumber;
  }
  if (user.providerData.length > 0) {
    const provideData = user.providerData[0];
    if (provideData == "phone") {
      data.mobile = provideData.phoneNumber;
    }
    if (provideData.providerId == "google.com" || provideData.providerId == "apple.com") {
      if (provideData.email) {
        data.email = provideData.email;
      }
      if (provideData.phoneNumber) {
        data.mobile = provideData.phoneNumber;
      }
      if (provideData.displayName) {
        if (provideData.displayName.split(" ").length > 0) {
          data.firstName = provideData.displayName.split(" ")[0];
          data.lastName = provideData.displayName.split(" ")[1];
        } else {
          data.firstName = provideData.displayName;
        }
      }
      if (provideData.photoURL) {
        data["profile_image"] = provideData.photoURL;
      }
    }
  }
  if (user.providerData.length > 0 && user.verifyId) {
    data.verifyId = user.verifyId;
  }

  return data;
};

const DB_READ_TIMEOUT_MS = 5000;

const hydrateSignedInProfile = async (dispatch, user) => {
  const { config, singleUserRef } = firebase;

  // 1. Try JS SDK database read (works when JS SDK auth is signed in)
  let profile = null;
  try {
    profile = await Promise.race([
      new Promise((resolve, reject) => {
        onValue(
          singleUserRef(user.uid),
          (snapshot) => resolve(snapshot.val()),
          (error) => reject(error),
          { onlyOnce: true }
        );
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("DB_READ_TIMEOUT")), DB_READ_TIMEOUT_MS)
      ),
    ]);
  } catch (dbError) {
  }

  // 2. Fallback: REST API with native auth token
  if (!profile) {
    try {
      profile = await readProfileViaRestApi(config.databaseURL, user.uid);
      if (profile) {
      }
    } catch (restError) {
    }
  }

  if (profile) {
    profile.uid = user.uid;
    dispatch({ type: FETCH_USER_SUCCESS, payload: profile });
    return profile;
  }

  // 3. Final fallback: check_auth_exists API (creates profile if needed)
  try {
    const data = buildProfileSeedFromUser(user);
    const response = await fetch("https://api.tasirimkurye.com/check_auth_exists", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Basic " + base64.encode(config.projectId),
      },
      body: JSON.stringify({ data: JSON.stringify(data) }),
    });
    const json = await response.json();

    if (json.uid) {
      dispatch({ type: FETCH_USER_SUCCESS, payload: json });
      return json;
    }

    const payload = {
      code: store.getState().languagedata.defaultLanguage.auth_error,
      message: json.error || store.getState().languagedata.defaultLanguage.not_logged_in,
    };
    dispatch({ type: FETCH_USER_FAILED, payload });
    throw payload;
  } catch (error) {
    const payload = normalizeActionError(
      error,
      store.getState().languagedata.defaultLanguage.auth_error
    );
    dispatch({ type: FETCH_USER_FAILED, payload });
    throw payload;
  }
};

const readProfileViaRestApi = async (databaseURL, uid) => {
  const idToken = await getNativeIdToken();
  if (!idToken) return null;
  const response = await fetch(
    `${databaseURL}/users/${uid}.json?auth=${idToken}`
  );
  if (!response.ok) return null;
  const profile = await response.json();
  if (profile) profile.uid = uid;
  return profile;
};

const signInWithNativeAuth = async (token) => {
  const { getAuth: getNativeAuth, signInWithCustomToken: nativeSignIn } = require('@react-native-firebase/auth');
  const nativeAuth = getNativeAuth();
  const userCredential = await nativeSignIn(nativeAuth, token);
  return userCredential;
};

const signInNativeWithEmailPassword = async (email, pass) => {
  const { getAuth: getNativeAuth, signInWithEmailAndPassword: nativeEmailSignIn } = require('@react-native-firebase/auth');
  const nativeAuth = getNativeAuth();
  return await nativeEmailSignIn(nativeAuth, email, pass);
};

const signOutNativeAuth = async () => {
  const { getAuth: getNativeAuth, signOut: nativeSignOut } = require('@react-native-firebase/auth');
  const nativeAuth = getNativeAuth();
  return await nativeSignOut(nativeAuth);
};

const signInWithCustomTokenAndTimeout = async (auth, token, timeoutMessage) => {
  // Try JS SDK first (works on web, may fail on React Native)
  try {
    const result = await new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject({
          code: "CUSTOM_TOKEN_TIMEOUT",
          message: timeoutMessage,
        });
      }, CUSTOM_TOKEN_SIGN_IN_TIMEOUT_MS);

      signInWithCustomToken(auth, token)
        .then((res) => {
          clearTimeout(timeoutId);
          resolve(res);
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          reject(err);
        });
    });
    try {
      await signInWithNativeAuth(token);
    } catch (nativeError) {
      console.error('[auth] Native custom token sync failed.', {
        code: nativeError?.code || null,
        message: nativeError?.message || String(nativeError),
      });
    }
    return result;
  } catch (error) {
    if (error?.code !== "auth/network-request-failed") {
      throw error;
    }
  }

  // Fallback: use @react-native-firebase/auth native module
  return await signInWithNativeAuth(token);
};

export const fetchUser = () => (dispatch) => {
  const { auth, config, singleUserRef } = firebase;
  let pendingExplicitSignOutBootstrap = false;

  // Never seed user identity from cache. User-scoped UI should only render
  // after a real auth session is confirmed.
  const seedCachedProfile = async () => {
    if (AsyncStorage) {
      try {
        const skipCachedProfileOnce = await AsyncStorage.getItem(SKIP_CACHED_PROFILE_ONCE_KEY);
        if (skipCachedProfileOnce) {
          await AsyncStorage.removeItem(SKIP_CACHED_PROFILE_ONCE_KEY);
          pendingExplicitSignOutBootstrap = true;
          console.log('[auth] Skipping cached profile seed after explicit sign out.');
          const nativeAuth = getNativeAuth();
          await Promise.allSettled([
            auth?.currentUser ? signOut(auth) : Promise.resolve(),
            nativeAuth?.currentUser ? signOutNativeAuth() : Promise.resolve(),
          ]);
        }
      } catch (e) {}
    }

    dispatch({
      type: FETCH_USER,
      payload: null,
    });

  onAuthStateChanged(auth, (user) => {
    const currentNativeUser = getNativeAuth()?.currentUser;

    if (pendingExplicitSignOutBootstrap) {
      if (user || currentNativeUser) {
        console.log('[auth] Ignoring persisted auth session after explicit sign out.');
        Promise.allSettled([
          user ? signOut(auth) : Promise.resolve(),
          currentNativeUser ? signOutNativeAuth() : Promise.resolve(),
        ]).then((results) => {
          if (results.some((result) => result.status === 'rejected')) {
            console.error('[auth] Forced bootstrap sign out had rejected operations.', results);
          }
        });
        return;
      }

      pendingExplicitSignOutBootstrap = false;
      dispatchNotLoggedInIfStillSignedOut(dispatch, auth);
      return;
    }

    if (user) {
      onValue(singleUserRef(user.uid), async (snapshot) => {
        if (snapshot.val()) {
          let profile = snapshot.val();
          profile.uid = user.uid;
          dispatch({
            type: FETCH_USER_SUCCESS,
            payload: profile,
          });
        } else {
          let data = {
            uid: user.uid,
            mobile: "",
            email: "",
            firstName: "",
            lastName: "",
            verifyId: "",
          };

          if (user.providerData.length == 0 && user.email) {
            data.email = user.email;
          }
          if (user.providerData.length > 0 && user.phoneNumber) {
            data.mobile = user.phoneNumber;
          }
          if (user.providerData.length > 0) {
            const provideData = user.providerData[0];
            if (provideData == "phone") {
              data.mobile = provideData.phoneNumber;
            }
            if (provideData.providerId == "google.com" || provideData.providerId == "apple.com") {
              if (provideData.email) {
                data.email = provideData.email;
              }
              if (provideData.phoneNumber) {
                data.mobile = provideData.phoneNumber;
              }
              if (provideData.displayName) {
                if (provideData.displayName.split(" ").length > 0) {
                  data.firstName = provideData.displayName.split(" ")[0];
                  data.lastName = provideData.displayName.split(" ")[1];
                } else {
                  data.firstName = provideData.displayName;
                }
              }
              if (provideData.photoURL) {
                data["profile_image"] = provideData.photoURL;
              }
            }
          }
          if (user.providerData.length > 0 && user.verifyId) {
            data.verifyId = user.verifyId;
          }

          const settings = store.getState().settingsdata.settings;
          let host = "https://api.tasirimkurye.com";
          let url = `${host}/check_auth_exists`;
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Basic " + base64.encode(config.projectId),
            },
            body: JSON.stringify({ data: JSON.stringify(data) }),
          });
          const json = await response.json();
          if (json.uid) {
            dispatch({
              type: FETCH_USER_SUCCESS,
              payload: json,
            });
          } else {
            const bootstrapStrings = getBootstrapAuthStrings();
            dispatch({
              type: FETCH_USER_FAILED,
              payload: { code: bootstrapStrings.authError, message: json.error || bootstrapStrings.notLoggedIn },
            });
          }
        }
      });
    } else {
      waitForNativeAuthUser()
        .then((nativeUser) => {
          const fallbackUser = currentNativeUser || nativeUser;
          if (!fallbackUser) {
            dispatchNotLoggedInIfStillSignedOut(dispatch, auth);
            return null;
          }
          return hydrateSignedInProfile(dispatch, fallbackUser);
        })
        .catch(() => {
          dispatchNotLoggedInIfStillSignedOut(dispatch, auth);
        });
    }
  });
  };

  seedCachedProfile();
};

export const validateReferer = async (referralId) => {
  const { config } = firebase;
  const response = await fetch(`https://api.tasirimkurye.com/validate_referrer`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      referralId: referralId,
    }),
  });
  const json = await response.json();
  return json;
};

export const checkUserExists = async (data) => {
  const { config } = firebase;

  const settings = store.getState().settingsdata.settings;
  let host = "https://api.tasirimkurye.com";
  let url = `${host}/check_user_exists`;
  
  if (!config || !config.projectId) {
    return { error: "API configuration error" };
  }
  
  const authHeader = "Basic " + base64.encode(config.projectId);
  
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": authHeader,
      },
      body: JSON.stringify({
        email: data.email,
        mobile: data.mobile,
      }),
    });
    
    const json = await response.json();
    return json;
  } catch (error) {
    return { error: "Network error" };
  }
};

export const mainSignUp = async (regData) => {
  const { config } = firebase;
  let url = `https://api.tasirimkurye.com/user_signup`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ regData: regData }),
  });
  const res = await response.json();
  return res;
};

export const updateProfileWithEmail = (profileData) => async (dispatch) => {
  const { config } = firebase;
  try {
    const settings = store.getState().settingsdata.settings;
    let host = "https://api.tasirimkurye.com";
    let url = `${host}/update_user_email`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Basic " + base64.encode(config.projectId),
      },
      body: JSON.stringify(profileData),
    });
    const result = await response.json();
    if (result.error) {
      return { success: false, error: result.error };
    }
  } catch (error) {
    return { success: false, error: error };
  }
};

export const requestPhoneOtpDevice = (verificationId) => async (dispatch) => {
  dispatch({
    type: REQUEST_OTP_SUCCESS,
    payload: verificationId,
  });
};

export const mobileSignIn = (verficationId, code) => (dispatch) => {
  const { auth, mobileAuthCredential } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null,
  });
  signInWithCredential(auth, mobileAuthCredential(verficationId, code))
    .then((user) => {
      //OnAuthStateChange takes care of Navigation
    })
    .catch((error) => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error,
      });
    });
};

export const saveAddresses = async (uid, location, name) => {
  const { singleUserRef } = firebase;
  onValue(
    child(singleUserRef(uid), "savedAddresses"),
    (savedAdd) => {
      if (savedAdd.val()) {
        let addresses = savedAdd.val();
        let didNotMatch = true;
        for (let key in addresses) {
          let entry = addresses[key];
          if (entry.name == name) {
            didNotMatch = false;
            update(child(singleUserRef(uid), "savedAddresses/" + key), {
              description: location.add,
              lat: location.lat,
              lng: location.lng,
              count: 1,
              name: name,
            });
            break;
          }
        }
        if (didNotMatch) {
          push(child(singleUserRef(uid), "savedAddresses"), {
            description: location.add,
            lat: location.lat,
            lng: location.lng,
            count: 1,
            name: name,
          });
        }
      } else {
        push(child(singleUserRef(uid), "savedAddresses"), {
          description: location.add,
          lat: location.lat,
          lng: location.lng,
          count: 1,
          name: name,
        });
      }
    },
    { onlyOnce: true }
  );
};

export const googleLogin = (idToken, accessToken) => (dispatch) => {
  const { auth, googleCredential } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null,
  });

  const credential = googleCredential(idToken, accessToken);
  signInWithCredential(auth, credential)
    .then((user) => {
      //OnAuthStateChange takes care of Navigation
    })
    .catch((error) => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error,
      });
    });
};

export const appleSignIn = (credentialData) => (dispatch) => {
  const { auth, appleProvider } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null,
  });
  if (credentialData) {
    const credential = appleProvider.credential(credentialData);
    signInWithCredential(auth, credential)
      .then((user) => {
        //OnAuthStateChange takes care of Navigation
      })
      .catch((error) => {
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: error,
        });
      });
  } else {
    signInWithPopup(auth, appleProvider)
      .then(function (result) {
        signInWithCredential(auth, result.credential)
          .then((user) => {
            //OnAuthStateChange takes care of Navigation
          })
          .catch((error) => {
            dispatch({
              type: USER_SIGN_IN_FAILED,
              payload: error,
            });
          });
      })
      .catch(function (error) {
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: error,
        });
      });
  }
};

export const signOff = () => async (dispatch) => {
  const { auth, config, singleUserRef, walletHistoryRef, userNotificationsRef } = firebase;
  const profile = store.getState().auth?.profile;
  const nativeAuth = getNativeAuth();
  const uid = getCurrentUid(auth);

  if (AsyncStorage) {
    try {
      await AsyncStorage.setItem(SKIP_CACHED_PROFILE_ONCE_KEY, '1');
      await AsyncStorage.removeItem(CACHED_PROFILE_KEY);
      const appNames = Array.from(new Set([
        auth?.app?.name,
        '[DEFAULT]',
      ].filter(Boolean)));
      const firebasePersistenceKeys = appNames.flatMap((appName) =>
        getFirebaseAuthPersistenceKeys(config, appName)
      );
      if (firebasePersistenceKeys.length > 0) {
        await AsyncStorage.multiRemove(firebasePersistenceKeys);
      }
    } catch (error) {
      console.error('[auth] Failed to clear cached profile during sign out.', {
        code: error?.code || null,
        message: error?.message || String(error),
      });
    }
  }

  if (uid) {
    off(singleUserRef(uid));
    off(walletHistoryRef(uid));
    off(userNotificationsRef(uid));
  }

  if (uid && profile?.usertype === "driver") {
    safeUpdate(singleUserRef(uid), { driverActiveStatus: false }).catch(() => {});
  }

  Promise.allSettled([
    auth ? signOut(auth) : Promise.resolve(),
    nativeAuth ? signOutNativeAuth() : Promise.resolve(),
  ]).then((results) => {
    const rejected = results.filter((result) => result.status === 'rejected');
    if (rejected.length > 0) {
      console.error('[auth] Sign out had rejected operations.', {
        rejected: rejected.map((item) => item.reason?.message || String(item.reason)),
      });
    }
  }).finally(() => {
    dispatch({
      type: USER_SIGN_OUT,
      payload: null,
    });
  });
};

export const updateProfile = (updateData) => async (dispatch) => {
  const { auth, singleUserRef, driverDocsRef, driverDocsRefBack, verifyIdImageRef, taxPlateRef } = firebase;

  const uid = getCurrentUid(auth);

  if (updateData.licenseImage) {
    updateData.licenseImage = await uploadProfileDocument(
      driverDocsRef(uid),
      `users/${uid}/license`,
      updateData.licenseImage,
      'image/jpeg'
    );
  }
  if (updateData.licenseImageBack) {
    updateData.licenseImageBack = await uploadProfileDocument(
      driverDocsRefBack(uid),
      `users/${uid}/licenseBack`,
      updateData.licenseImageBack,
      'image/jpeg'
    );
  }
  if (updateData.verifyIdImage) {
    updateData.verifyIdImage = await uploadProfileDocument(
      verifyIdImageRef(uid),
      `users/${uid}/verifyIdImage`,
      updateData.verifyIdImage,
      'image/jpeg'
    );
  }
  if (updateData.taxPlate) {
    updateData.taxPlate = await uploadProfileDocument(
      taxPlateRef(uid),
      `users/${uid}/taxPlate`,
      updateData.taxPlate,
      'image/jpeg'
    );
  }

  await safeUpdate(singleUserRef(uid), updateData);
};

export const updateProfileImage = async (imageFile) => {
  const { auth, singleUserRef, profileImageRef } = firebase;

  const uid = getCurrentUid(auth);

  const url = await uploadProfileDocument(
    profileImageRef(uid),
    `users/${uid}/profileImage`,
    imageFile,
    'image/jpeg'
  );
  await safeUpdate(singleUserRef(uid), {
    profile_image: url,
  });
};

export const updateWebProfileImage = async (imageBlob) => {
  const { auth, singleUserRef, profileImageRef } = firebase;

  const uid = getCurrentUid(auth);

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), { profile_image: image });
};
export const updateCustomerProfileImage = async (imageBlob, id) => {
  const { singleUserRef, profileImageRef } = firebase;

  const uid = id;

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), { profile_image: image });
};

export const updatePushToken = (token, platform) => {
  const { auth, singleUserRef } = firebase;

  const uid = getCurrentUid(auth);
  if (!uid) return;

  safeUpdate(singleUserRef(uid), {
    pushToken: token,
    userPlatform: platform,
  });
};

export const clearLoginError = () => (dispatch) => {
  dispatch({
    type: CLEAR_LOGIN_ERROR,
    payload: null,
  });
};

export const fetchWalletHistory = () => (dispatch) => {
  const { auth, walletHistoryRef } = firebase;

  const uid = getCurrentUid(auth);

  onValue(walletHistoryRef(uid), (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const arr = Object.keys(data).map((i) => {
        data[i].id = i;
        return data[i];
      });
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: arr.reverse(),
      });
    }
  });
};
export const fetchUserWalletHistory = (userId) => (dispatch) => {
  const { auth, walletHistoryRef } = firebase;

  const uid = userId;

  onValue(walletHistoryRef(uid), (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const arr = Object.keys(data).map((i) => {
        data[i].id = i;
        return data[i];
      });
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: arr.reverse(),
      });
    } else {
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: [],
      });
    }
  });
};

export const sendResetMail = (email) => async (dispatch) => {
  const { authRef } = firebase;

  dispatch({
    type: SEND_RESET_EMAIL,
    payload: email,
  });

  try {
    const auth = authRef();
    auth.languageCode = "tr";
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const verifyEmailPassword = (email, pass) => async (dispatch) => {
  const { authRef } = firebase;

  signInWithEmailAndPassword(authRef(), email, pass)
    .then(async (user) => {
      try {
        await signInNativeWithEmailPassword(email, pass);
      } catch (nativeError) {
        console.error('[auth] Native email/password sync failed.', {
          email,
          code: nativeError?.code || null,
          message: nativeError?.message || String(nativeError),
        });
      }
      //OnAuthStateChange takes care of Navigation
    })
    .catch((error) => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error,
      });
    });
};

export const requestMobileOtp = (mobile, appHash) => async (dispatch) => {
  const requestTimeoutMessage = "SMS dogrulama istegi zaman asimina ugradi. Lutfen tekrar deneyin.";
  dispatch({
    type: REQUEST_OTP,
    payload: true,
  });

  console.log('[login-otp] requestMobileOtp dispatched.', {
    mobile: maskMobileForLog(mobile),
    hasAppHash: !!appHash,
  });

  const settings = store.getState().settingsdata.settings;
  let host = "https://api.tasirimkurye.com";
  let url = `${host}/request_mobile_otp`;
  try {
    const result = await fetchJsonWithTimeout(
      url,
      {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mobile: mobile,
        ...(appHash ? { appHash } : {}),
      }),
      },
      OTP_REQUEST_TIMEOUT_MS,
      requestTimeoutMessage
    );

    if (result.success) {
      console.log('[login-otp] requestMobileOtp success response.', {
        mobile: maskMobileForLog(mobile),
        success: !!result.success,
      });
      dispatch({
        type: REQUEST_OTP_SUCCESS,
        payload: true,
      });
    } else {
      console.warn('[login-otp] requestMobileOtp failed response.', {
        mobile: maskMobileForLog(mobile),
        error: result?.error || null,
      });
      dispatch({
        type: REQUEST_OTP_FAILED,
        payload: normalizeActionError(result.error, requestTimeoutMessage),
      });
    }
  } catch (error) {
    console.error('[login-otp] requestMobileOtp request threw.', {
      mobile: maskMobileForLog(mobile),
      code: error?.code || null,
      message: error?.message || String(error),
    });
    dispatch({
      type: REQUEST_OTP_FAILED,
      payload: normalizeActionError(error, requestTimeoutMessage),
    });
  }
};

export const requestCall = async (mobile,caller) => {
  const { config } = firebase;
  const settings = store.getState().settingsdata.settings;
  let host = "https://api.tasirimkurye.com";
  let url = `${host}/netgsm-santral`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ called: mobile,caller:caller }),
    });
    const result = await response.json();
    return result;
  } catch (error) {
  }
};

export const verifyMobileOtp = (mobile, otp) => async (dispatch) => {
  const { auth, config } = firebase;
  const verifyTimeoutMessage = "SMS dogrulama suresi doldu veya istek zaman asimina ugradi. Lutfen kodu tekrar isteyin.";
  const signInTimeoutMessage = "Giris dogrulamasi tamamlanamadi. Lutfen tekrar deneyin.";
  const body = {
    mobile: mobile,
    otp: otp,
  };
  try {
    const settings = store.getState().settingsdata.settings;
    let host = "https://api.tasirimkurye.com";
    let url = `${host}/verify_mobile_otp`;
    const result = await fetchJsonWithTimeout(
      url,
      {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      },
      OTP_VERIFY_TIMEOUT_MS,
      verifyTimeoutMessage
    );

    if (result.token) {
      dispatch({
        type: USER_SIGN_IN,
        payload: null,
      });

      const token = typeof result.token === "string" ? result.token.trim() : result.token;
      const userCredential = await signInWithCustomTokenAndTimeout(auth, token, signInTimeoutMessage);
      await hydrateSignedInProfile(dispatch, userCredential.user);
    } else {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: normalizeActionError(result.error, verifyTimeoutMessage),
      });
    }
  } catch (error) {
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: normalizeActionError(error, verifyTimeoutMessage),
    });
  }
};

export const updateAuthMobile = async (mobile, otp) => {
  const { auth, config } = firebase;

  const uid = getCurrentUid(auth);
  const body = {
    uid: uid,
    mobile: mobile,
    otp: otp,
  };

  const settings = store.getState().settingsdata.settings;
  let host = "https://api.tasirimkurye.com";
  let url = `${host}/update_auth_mobile`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (result.success) {
      return { success: true };
    } else {
      return { success: false, error: result.error };
    }
  } catch (error) {
    return { success: false, error: error };
  }
};
